/**
 * Geldbeträge, wie sie in diesem Haus geschrieben werden.
 *
 * DER SERVER RECHNET UND SCHICKT DEZIMALTEXT. `'2000.00'` ist die Zahl, nicht
 * ihre Darstellung: Punkt als Dezimaltrenner, keine Tausenderpunkte, kein
 * Zeichen. So gehört sie in die Leitung — und so gehört sie nicht auf den
 * Bildschirm.
 *
 * Auftraggeber am 12.09.2026: „Beträge korrekt formatieren, inkl. Euro
 * Zeichen." Für die Belege steht das seit `belegausgabe.service.ts`; hier
 * steht dieselbe Regel für die Masken, mit derselben Festlegung:
 *
 *   * deutsche Schreibweise — Komma als Dezimaltrenner, Punkt als
 *     Tausendertrenner (2.10, Tabelle 5);
 *   * zwei Nachkommastellen, weil ein Beleg auf Cent lautet (RND-05);
 *   * ein GESCHÜTZTES Leerzeichen vor dem Euro-Zeichen, damit die Einheit
 *     nicht allein in die nächste Zeile rutscht;
 *   * was keine Zahl ist, geht unverändert durch. Ein Strich bleibt ein
 *     Strich, und aus `null` wird kein „0,00 €" — das wäre eine Behauptung
 *     über Geld, die niemand aufgestellt hat.
 */
const SATZ = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });

const EURO = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function alsEuro(wert: string | null | undefined): string {
  if (wert === null || wert === undefined || wert.trim() === '') return '—';
  const zahl = Number(wert);
  if (!Number.isFinite(zahl)) return wert;
  return `${EURO.format(zahl)}\u00A0€`;
}

const SATZEURO = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

/**
 * Ein Satz in Euro — ein Honorar je Kilometer, ein Zuschlag je Einheit.
 *
 * Die Honorartabelle führt vier Nachkommastellen (`numeric(14,4)`), weil ein
 * Satz wie 0,3750 € je Kilometer sonst nicht darstellbar wäre. `alsEuro`
 * rundete ihn auf Cent und zeigte einen anderen Satz als den vereinbarten.
 * Hier stehen mindestens zwei und höchstens vier Stellen, sonst dieselbe
 * Schreibweise. Was ein Beleg ausweist, bleibt bei `alsEuro` (RND-05).
 */
export function alsEurosatz(wert: string | null | undefined): string {
  if (wert === null || wert === undefined || wert.trim() === '') return '—';
  const zahl = Number(wert);
  if (!Number.isFinite(zahl)) return wert;
  return `${SATZEURO.format(zahl)}\u00A0€`;
}

/**
 * Ein Prozentsatz ohne die Nullen, die niemand liest.
 *
 * Die Staffel führt vier Nachkommastellen, weil ein Satz wie 12,5 % sonst
 * nicht darstellbar wäre. Auf dem Bildschirm steht „15 %" und nicht
 * „15.0000 %".
 */
export function alsProzent(wert: string | null | undefined): string {
  if (wert === null || wert === undefined || wert.trim() === '') return '—';
  const zahl = Number(wert);
  if (!Number.isFinite(zahl)) return wert;
  return `${SATZ.format(zahl)}\u00A0%`;
}

/**
 * Eine Menge, wie sie gelesen wird — „1.000" ist die Eins.
 *
 * `numeric(12,3)` kommt als „1.000" aus der Datenbank. Auf dem Bildschirm
 * gelesen heißt das TAUSEND, gemeint ist EINS. Nachlaufende Nullen fallen
 * deshalb weg, die Stellen, die wirklich dastehen, bleiben: „1,5" bleibt
 * anderthalb.
 *
 * Dieselbe Regel wie `alsMenge` in der Belegausgabe (API) — sie setzt sie für
 * das gedruckte Dokument, diese hier für die Maske.
 */
export function alsMenge(wert: string | number | null | undefined): string {
  if (wert === null || wert === undefined || String(wert).trim() === '') return '—';
  const zahl = Number(wert);
  if (!Number.isFinite(zahl)) return String(wert);
  const text = String(wert);
  const punkt = text.indexOf('.');
  /* Nur die Stellen, die einen Wert tragen: aus „1.500" wird „1,5". */
  const stellen = punkt === -1 ? 0 : text.slice(punkt + 1).replace(/0+$/, '').length;
  return zahl.toLocaleString('de-DE', {
    minimumFractionDigits: stellen,
    maximumFractionDigits: stellen,
  });
}

/**
 * Die Mengeneinheit in der Form, in der sie GELESEN wird (RNG-13, PRD-42).
 *
 * „4 Platz" liest sich wie ein Fehler, „4 Plätze" wie ein Beleg. Abkürzungen
 * bleiben unverändert: „4 Stks" wäre falsch.
 *
 * WAS HIER NICHT STEHT, GEHT UNVERÄNDERT DURCH — dieselbe Zurückhaltung wie
 * beim UN/ECE-Code in `fach.mengeneinheit`: lieber die eigene Schreibweise
 * als eine geratene.
 *
 * ============================================================================
 * DIESELBE TABELLE STEHT IN DER BELEGAUSGABE — UND DAS IST ABSICHT
 * ============================================================================
 *
 * `alsEinheit` in `apps/api/.../belegausgabe.service.ts` beugt dieselben
 * Wörter für das gedruckte Dokument. Der naheliegende Weg — eine gemeinsame
 * Tabelle in `@crm/kontrakte` — ist VERSPERRT: Das Paket ist CommonJS und
 * bringt zod mit; ein WERTimport daraus zöge zod in das Browserbündel (siehe
 * `Arbeitsflaeche.tsx` und `buendeltrennung.test.ts`). Die Oberfläche
 * importiert daraus deshalb nur Typen.
 *
 * Wer eine Einheit nachträgt, trägt sie an BEIDEN Stellen nach — sonst steht
 * auf dem Bildschirm „4 Plätze" und auf der Auftragsbestätigung „4 Platz".
 * Der richtige Ort wäre ein eigenes Darstellungspaket ohne Abhängigkeiten;
 * dafür gibt es bisher zu wenig gemeinsamen Stoff.
 */
const EINHEITENFORM: Readonly<Record<string, readonly [string, string]>> = {
  Stk: ['Stk', 'Stk'],
  Std: ['Std', 'Std'],
  km: ['km', 'km'],
  Platz: ['Platz', 'Plätze'],
  Person: ['Person', 'Personen'],
  Termin: ['Termin', 'Termine'],
  Block: ['Block', 'Blöcke'],
  Tag: ['Tag', 'Tage'],
  Monat: ['Monat', 'Monate'],
  Jahr: ['Jahr', 'Jahre'],
  Stueck: ['Stück', 'Stück'],
  Stunde: ['Stunde', 'Stunden'],
  /* Der Artikelstamm schreibt sie ohne e; gelesen wird das Wort. */
  Pauschal: ['Pauschale', 'Pauschalen'],
  Pauschale: ['Pauschale', 'Pauschalen'],
};

/**
 * Genau EINS ist Einzahl, alles andere Mehrzahl — auch 0 und 0,5. So macht es
 * das Deutsche: „0 Plätze", „0,5 Stunden", „1 Platz".
 */
function beugeEinheit(
  menge: string | number | null | undefined,
  einheit: string | null | undefined,
): string | null {
  if (einheit === null || einheit === undefined) return null;
  const form = EINHEITENFORM[einheit];
  if (form === undefined) return einheit;
  const zahl = menge === null || menge === undefined ? Number.NaN : Number(menge);
  return zahl === 1 ? form[0] : form[1];
}

/**
 * Menge und Einheit als ein Stück — „1 Termin", „4 Plätze".
 *
 * Auftraggeber am 15.09.2026: „Auf dem Beleg soll der Termin stehen." Auf dem
 * Beleg steht sie seit Migration 0210; in der Maske stand bis dahin die Zahl
 * allein, und beim Exklusivtermin ist eine Zahl ohne Einheit bedeutungslos:
 * ein Training, zwölf Plätze, ein Termin — die Eins sagt nicht, welches davon.
 *
 * Das geschützte Leerzeichen hält beide zusammen; in einer schmalen Spalte
 * stünde die Einheit sonst allein in der nächsten Zeile.
 */
export function alsMengenangabe(
  menge: string | number | null | undefined,
  einheit: string | null | undefined,
): string {
  const zahl = alsMenge(menge);
  const wort = beugeEinheit(menge, einheit);
  return wort === null || zahl === '—' ? zahl : `${zahl}\u00A0${wort}`;
}
